/**
 * Affiliate Actions
 * =================
 *
 * Redux actions for the manual affiliate system
 */

import axios from 'axios';
import {
    // Affiliate Assignment
    GET_PLAYER_AFFILIATES_REQUEST,
    GET_PLAYER_AFFILIATES_SUCCESS,
    GET_PLAYER_AFFILIATES_FAIL,
    ASSIGN_AFFILIATE_REQUEST,
    ASSIGN_AFFILIATE_SUCCESS,
    ASSIGN_AFFILIATE_FAIL,
    UPDATE_AFFILIATE_ASSIGNMENT_REQUEST,
    UPDATE_AFFILIATE_ASSIGNMENT_SUCCESS,
    UPDATE_AFFILIATE_ASSIGNMENT_FAIL,
    REMOVE_AFFILIATE_ASSIGNMENT_REQUEST,
    REMOVE_AFFILIATE_ASSIGNMENT_SUCCESS,
    REMOVE_AFFILIATE_ASSIGNMENT_FAIL,
    GET_AFFILIATE_REFERRALS_REQUEST,
    GET_AFFILIATE_REFERRALS_SUCCESS,
    GET_AFFILIATE_REFERRALS_FAIL,

    // Session Hours
    LOG_SESSION_HOURS_REQUEST,
    LOG_SESSION_HOURS_SUCCESS,
    LOG_SESSION_HOURS_FAIL,
    GET_PLAYER_SESSION_HOURS_REQUEST,
    GET_PLAYER_SESSION_HOURS_SUCCESS,
    GET_PLAYER_SESSION_HOURS_FAIL,
    VOID_SESSION_LOG_REQUEST,
    VOID_SESSION_LOG_SUCCESS,
    VOID_SESSION_LOG_FAIL,

    // Commission Tiers
    GET_COMMISSION_TIERS_REQUEST,
    GET_COMMISSION_TIERS_SUCCESS,
    GET_COMMISSION_TIERS_FAIL,
    CREATE_COMMISSION_TIER_REQUEST,
    CREATE_COMMISSION_TIER_SUCCESS,
    CREATE_COMMISSION_TIER_FAIL,
    UPDATE_COMMISSION_TIER_REQUEST,
    UPDATE_COMMISSION_TIER_SUCCESS,
    UPDATE_COMMISSION_TIER_FAIL,
    DELETE_COMMISSION_TIER_REQUEST,
    DELETE_COMMISSION_TIER_SUCCESS,
    DELETE_COMMISSION_TIER_FAIL,
    SEED_DEFAULT_TIERS_REQUEST,
    SEED_DEFAULT_TIERS_SUCCESS,
    SEED_DEFAULT_TIERS_FAIL,

    // Commission Calculation
    PREVIEW_COMMISSION_REQUEST,
    PREVIEW_COMMISSION_SUCCESS,
    PREVIEW_COMMISSION_FAIL,
    CALCULATE_COMMISSION_REQUEST,
    CALCULATE_COMMISSION_SUCCESS,
    CALCULATE_COMMISSION_FAIL,
    GET_PENDING_COMMISSIONS_REQUEST,
    GET_PENDING_COMMISSIONS_SUCCESS,
    GET_PENDING_COMMISSIONS_FAIL,
    APPROVE_COMMISSION_REQUEST,
    APPROVE_COMMISSION_SUCCESS,
    APPROVE_COMMISSION_FAIL,
    REJECT_COMMISSION_REQUEST,
    REJECT_COMMISSION_SUCCESS,
    REJECT_COMMISSION_FAIL,
    BULK_APPROVE_COMMISSIONS_REQUEST,
    BULK_APPROVE_COMMISSIONS_SUCCESS,
    BULK_APPROVE_COMMISSIONS_FAIL,

    // Potential Affiliates
    GET_POTENTIAL_AFFILIATES_REQUEST,
    GET_POTENTIAL_AFFILIATES_SUCCESS,
    GET_POTENTIAL_AFFILIATES_FAIL,
} from '../constants/affiliateConstants';

const baseUrl = process.env.REACT_APP_API_URL;

// ============================================================================
// AFFILIATE ASSIGNMENT ACTIONS
// ============================================================================

/**
 * Get all affiliates assigned to a player
 */
export const getPlayerAffiliates = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_AFFILIATES_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.get(
            `${baseUrl}/affiliate/player/${playerId}/affiliates`,
            config
        );

        dispatch({
            type: GET_PLAYER_AFFILIATES_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_PLAYER_AFFILIATES_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Assign affiliate to a player
 */
export const assignAffiliateToPlayer = (playerId, affiliateData) => async (dispatch, getState) => {
    try {
        dispatch({ type: ASSIGN_AFFILIATE_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(
            `${baseUrl}/affiliate/player/${playerId}/assign`,
            affiliateData,
            config
        );

        dispatch({
            type: ASSIGN_AFFILIATE_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: ASSIGN_AFFILIATE_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Update affiliate assignment
 */
export const updateAffiliateAssignment = (assignmentId, updateData) => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_AFFILIATE_ASSIGNMENT_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put(
            `${baseUrl}/affiliate/assignment/${assignmentId}`,
            updateData,
            config
        );

        dispatch({
            type: UPDATE_AFFILIATE_ASSIGNMENT_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: UPDATE_AFFILIATE_ASSIGNMENT_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Remove affiliate assignment
 */
export const removeAffiliateAssignment = (assignmentId, reason = null) => async (dispatch, getState) => {
    try {
        dispatch({ type: REMOVE_AFFILIATE_ASSIGNMENT_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
            data: { reason }
        };

        const { data } = await axios.delete(
            `${baseUrl}/affiliate/assignment/${assignmentId}`,
            config
        );

        dispatch({
            type: REMOVE_AFFILIATE_ASSIGNMENT_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: REMOVE_AFFILIATE_ASSIGNMENT_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Get all referrals for an affiliate
 */
export const getAffiliateReferrals = (affiliateId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_AFFILIATE_REFERRALS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.get(
            `${baseUrl}/affiliate/${affiliateId}/referrals`,
            config
        );

        dispatch({
            type: GET_AFFILIATE_REFERRALS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_AFFILIATE_REFERRALS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// SESSION HOURS ACTIONS
// ============================================================================

/**
 * Log session hours for a player
 */
export const logSessionHours = (hoursData) => async (dispatch, getState) => {
    try {
        dispatch({ type: LOG_SESSION_HOURS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(
            `${baseUrl}/affiliate/session-hours`,
            hoursData,
            config
        );

        dispatch({
            type: LOG_SESSION_HOURS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: LOG_SESSION_HOURS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Get session hours for a player
 */
export const getPlayerSessionHours = (playerId, startDate, endDate) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_SESSION_HOURS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        let url = `${baseUrl}/affiliate/session-hours/player/${playerId}`;
        if (startDate && endDate) {
            url += `?startDate=${startDate}&endDate=${endDate}`;
        }

        const { data } = await axios.get(url, config);

        dispatch({
            type: GET_PLAYER_SESSION_HOURS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_PLAYER_SESSION_HOURS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Void a session hours log
 */
export const voidSessionHoursLog = (logId, reason) => async (dispatch, getState) => {
    try {
        dispatch({ type: VOID_SESSION_LOG_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
            data: { reason }
        };

        const { data } = await axios.delete(
            `${baseUrl}/affiliate/session-hours/${logId}`,
            config
        );

        dispatch({
            type: VOID_SESSION_LOG_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: VOID_SESSION_LOG_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// COMMISSION TIER ACTIONS
// ============================================================================

/**
 * Get all commission tiers
 */
export const getCommissionTiers = () => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_COMMISSION_TIERS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.get(`${baseUrl}/affiliate/tiers`, config);

        dispatch({
            type: GET_COMMISSION_TIERS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_COMMISSION_TIERS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Create commission tier
 */
export const createCommissionTier = (tierData) => async (dispatch, getState) => {
    try {
        dispatch({ type: CREATE_COMMISSION_TIER_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(`${baseUrl}/affiliate/tiers`, tierData, config);

        dispatch({
            type: CREATE_COMMISSION_TIER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: CREATE_COMMISSION_TIER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Update commission tier
 */
export const updateCommissionTier = (tierId, tierData) => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_COMMISSION_TIER_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put(`${baseUrl}/affiliate/tiers/${tierId}`, tierData, config);

        dispatch({
            type: UPDATE_COMMISSION_TIER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: UPDATE_COMMISSION_TIER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Delete commission tier
 */
export const deleteCommissionTier = (tierId) => async (dispatch, getState) => {
    try {
        dispatch({ type: DELETE_COMMISSION_TIER_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.delete(`${baseUrl}/affiliate/tiers/${tierId}`, config);

        dispatch({
            type: DELETE_COMMISSION_TIER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: DELETE_COMMISSION_TIER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Seed default tiers
 */
export const seedDefaultTiers = () => async (dispatch, getState) => {
    try {
        dispatch({ type: SEED_DEFAULT_TIERS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(`${baseUrl}/affiliate/tiers/seed`, {}, config);

        dispatch({
            type: SEED_DEFAULT_TIERS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: SEED_DEFAULT_TIERS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// COMMISSION CALCULATION ACTIONS
// ============================================================================

/**
 * Preview commission calculation
 */
export const previewCommissionCalculation = (periodData) => async (dispatch, getState) => {
    try {
        dispatch({ type: PREVIEW_COMMISSION_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(`${baseUrl}/affiliate/commission/preview`, periodData, config);

        dispatch({
            type: PREVIEW_COMMISSION_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: PREVIEW_COMMISSION_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Calculate and create commissions
 */
export const calculateAndCreateCommissions = (periodData) => async (dispatch, getState) => {
    try {
        dispatch({ type: CALCULATE_COMMISSION_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(`${baseUrl}/affiliate/commission/calculate`, periodData, config);

        dispatch({
            type: CALCULATE_COMMISSION_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: CALCULATE_COMMISSION_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Get pending commissions
 */
export const getPendingCommissions = (type = null) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PENDING_COMMISSIONS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        let url = `${baseUrl}/affiliate/commission/pending`;
        if (type) {
            url += `?type=${type}`;
        }

        const { data } = await axios.get(url, config);

        dispatch({
            type: GET_PENDING_COMMISSIONS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_PENDING_COMMISSIONS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Approve commission
 */
export const approveCommission = (commissionId, remarks = null) => async (dispatch, getState) => {
    try {
        dispatch({ type: APPROVE_COMMISSION_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put(
            `${baseUrl}/affiliate/commission/${commissionId}/approve`,
            { remarks },
            config
        );

        dispatch({
            type: APPROVE_COMMISSION_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: APPROVE_COMMISSION_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Reject commission
 */
export const rejectCommission = (commissionId, reason) => async (dispatch, getState) => {
    try {
        dispatch({ type: REJECT_COMMISSION_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put(
            `${baseUrl}/affiliate/commission/${commissionId}/reject`,
            { reason },
            config
        );

        dispatch({
            type: REJECT_COMMISSION_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: REJECT_COMMISSION_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Bulk approve commissions
 */
export const bulkApproveCommissions = (commissionIds, remarks = null) => async (dispatch, getState) => {
    try {
        dispatch({ type: BULK_APPROVE_COMMISSIONS_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put(
            `${baseUrl}/affiliate/commission/bulk-approve`,
            { commissionIds, remarks },
            config
        );

        dispatch({
            type: BULK_APPROVE_COMMISSIONS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: BULK_APPROVE_COMMISSIONS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

/**
 * Get potential affiliates for dropdown (users who can be affiliates)
 */
export const getPotentialAffiliates = () => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_POTENTIAL_AFFILIATES_REQUEST });

        const { userLogin: { userInfo } } = getState();
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        // Use existing endpoint to get users who can be affiliates
        const { data } = await axios.get(`${baseUrl}/users/affiliates/list`, config);

        dispatch({
            type: GET_POTENTIAL_AFFILIATES_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_POTENTIAL_AFFILIATES_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};
